import type { QuoteLine, QuotePayload } from './api';

/*
 * Changements du panier appliqués à l'écran AVANT la réponse du serveur. Le
 * serveur reste la référence : sa réponse remplace ces valeurs (prix d'un palier
 * de quantité, prix refusé…). Les totaux sont calculés comme le sien (quoteTotals).
 */

const cents = (v: string) => Math.round(Number(v) * 100);
const money = (c: number) => (c / 100).toFixed(2);

export function totalsOf(lines: QuoteLine[]): QuotePayload['totals'] {
  const subtotal = lines.reduce((s, l) => s + cents(l.unitPrice) * l.qty, 0);
  const cores = lines.reduce((s, l) => s + cents(l.coreCost) * l.qty, 0);
  return {
    subtotal: money(subtotal),
    cores: money(cores),
    total: money(subtotal + cores),
    itemCount: lines.reduce((s, l) => s + l.qty, 0),
  };
}

function withLines(data: QuotePayload, lines: QuoteLine[]): QuotePayload {
  return { ...data, lines, totals: totalsOf(lines) };
}

/** Nouvelle quantité, au prix connu : le prix du palier arrive avec la réponse. */
export function setLineQty(data: QuotePayload, lineId: number, qty: number): QuotePayload {
  return withLines(
    data,
    data.lines.map((l) => (l.id === lineId ? { ...l, qty } : l)),
  );
}

export function dropLine(data: QuotePayload, lineId: number): QuotePayload {
  return withLines(
    data,
    data.lines.filter((l) => l.id !== lineId),
  );
}

export type NewLine = {
  partNum: string;
  mfgCode: string;
  title: string;
  brand: string | null;
  qty: number;
  /** Prix montré dans la recherche, ou prix saisi pour un article qu'Epicor ne tarife pas. */
  unitPrice: string;
  listPrice: string | null;
  coreCost: string;
  /** Faux : Epicor ne tarife pas l'article, le prix est celui saisi par le rep. */
  priced: boolean;
};

/**
 * Ajout d'un article : ligne provisoire (id négatif, `tempId`), ou quantité
 * additionnée si l'article est déjà au panier (comme le serveur, plafond 9999).
 */
export function addLine(data: QuotePayload, item: NewLine, tempId: number): QuotePayload {
  const existing = data.lines.find((l) => l.partNum === item.partNum && l.mfgCode === item.mfgCode);
  if (existing) return setLineQty(data, existing.id, Math.min(existing.qty + item.qty, 9999));
  return withLines(data, [
    ...data.lines,
    {
      id: tempId,
      partNum: item.partNum,
      mfgCode: item.mfgCode,
      title: item.title,
      brand: item.brand,
      qty: item.qty,
      epicorPrice: item.priced ? item.unitPrice : null,
      listPrice: item.listPrice,
      unitPrice: item.unitPrice,
      priceSource: item.priced ? 'epicor' : 'special_quote',
      coreCost: item.coreCost,
    },
  ]);
}

/** Montant saisi : « 12,5 » → « 12.50 », null s'il est invalide (même règle que le serveur). */
export function parseMoney(v: string): string | null {
  const s = v.trim().replace(',', '.');
  if (!/^\d{1,7}(\.\d{1,2})?$/.test(s) || Number(s) <= 0) return null;
  return money(cents(s));
}

export type PriceChange = { unitPrice: string } | { resetPrice: true };

/** Prix spécial saisi, ou retour au dernier prix Epicor connu de la ligne. */
export function setLinePrice(data: QuotePayload, lineId: number, change: PriceChange): QuotePayload {
  return withLines(
    data,
    data.lines.map((l) => {
      if (l.id !== lineId) return l;
      if ('resetPrice' in change) return { ...l, priceSource: 'epicor', unitPrice: l.epicorPrice ?? l.unitPrice };
      return { ...l, priceSource: 'special_quote', unitPrice: parseMoney(change.unitPrice) ?? l.unitPrice };
    }),
  );
}
